import { useCallback, useEffect, useState } from 'react';
import { clearDraft as clearPersistedDraft, loadDraft, saveDraft } from './persistence';
import type { Attachment } from '@/lib/types';

const SAVE_DEBOUNCE_MS = 500;

export interface UseAgentDraftReturn {
  draftText: string;
  draftAttachments: Attachment[];
  /**
   * Bind to the ChatInput `key` so the input remounts (and re-reads
   * `initialValue` / `initialAttachments`) once the persisted draft has been
   * loaded. ChatInput captures these props on mount only, so without a key
   * bump the restored text would never reach the textarea.
   */
  chatInputKey: number;
  setDraftText: (text: string) => void;
  setDraftAttachments: (attachments: Attachment[]) => void;
  clearDraft: () => void;
}

/**
 * Client-side persistence for the agent thread's chat-input draft.
 *
 * Each agent thread keeps its own draft so switching between tabs (which
 * remounts `AgentStreamView` via its agent-id key) doesn't lose the user's
 * in-flight composition.
 *
 * Pass `enabled={false}` when the input is externally driven (replay mode):
 * in that case we don't want to read or write per-agent drafts.
 */
export function useAgentDraft(agentId: string, enabled: boolean = true): UseAgentDraftReturn {
  const [draftText, setDraftTextState] = useState('');
  const [draftAttachments, setDraftAttachmentsState] = useState<Attachment[]>([]);
  const [chatInputKey, setChatInputKey] = useState(0);
  // When disabled (replay), there's nothing to load — start "loaded" so the
  // save effect's gate short-circuits via the `!enabled` check below.
  // When enabled, start false and flip to true once the async load resolves;
  // this prevents the save effect from clobbering persisted data with the
  // initial empty state before the load returns. AgentStreamView remounts
  // when `agent.id` changes (key={agent.id}), so we never need to reset
  // this flag mid-life.
  const [hasLoaded, setHasLoaded] = useState(!enabled);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    loadDraft(agentId)
      .then((data) => {
        if (cancelled) return;
        if (data && (data.text || data.attachments.length > 0)) {
          setDraftTextState(data.text);
          setDraftAttachmentsState(data.attachments);
          setChatInputKey((k) => k + 1);
        }
        setHasLoaded(true);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn('Failed to load agent thread draft:', error);
        setHasLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, enabled]);

  useEffect(() => {
    if (!enabled || !hasLoaded) return;
    const timeoutId = setTimeout(() => {
      if (draftText.length > 0 || draftAttachments.length > 0) {
        saveDraft(agentId, { text: draftText, attachments: draftAttachments });
      } else {
        clearPersistedDraft(agentId);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [enabled, hasLoaded, agentId, draftText, draftAttachments]);

  const setDraftText = useCallback((text: string) => {
    setDraftTextState(text);
  }, []);

  const setDraftAttachments = useCallback((attachments: Attachment[]) => {
    setDraftAttachmentsState(attachments);
  }, []);

  const clearDraft = useCallback(() => {
    setDraftTextState('');
    setDraftAttachmentsState([]);
    // Clear storage immediately too — the debounced save effect would
    // otherwise be the only writer, and it won't fire if the component
    // unmounts within the debounce window (e.g. user sends then switches
    // agents right away).
    if (enabled) clearPersistedDraft(agentId);
  }, [agentId, enabled]);

  return {
    draftText,
    draftAttachments,
    chatInputKey,
    setDraftText,
    setDraftAttachments,
    clearDraft,
  };
}
